import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useSite } from '../hooks/useSites';
import { useToast } from '../hooks/useToast';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { getContentId } from '../types';
import FormField from '../components/FormField';
import HelpSidebar from '../components/HelpSidebar';
import '../styles/editor.css';

const seoFields = {
  metaTitle: { type: 'text' as const, label: 'SEO Title', maxLength: 70, placeholder: 'My Business Name', hint: 'Custom title shown in search engine results and browser tabs. If left empty, your site name is used. Keep it under 60 characters for best results.' },
  metaDescription: { type: 'textarea' as const, label: 'SEO Description', maxLength: 160, placeholder: 'A short description of your business for search engines...', hint: 'The description shown under your title in Google search results. Keep it under 160 characters. Make it compelling — this is what convinces people to click.' },
  ogImage: { type: 'image' as const, label: 'Social Sharing Image', hint: 'The image shown when your site is shared on Facebook, Twitter, LinkedIn, etc. Use a landscape image at least 1200×630px for best results.' },
  googleVerification: { type: 'text' as const, label: 'Google Verification Code', placeholder: 'abc123...', hint: 'The verification code from Google Search Console. Paste just the code (not the full meta tag). This proves you own your site so you can see it in Google\'s tools.' },
};

const seoHelp = {
  title: 'Search Engine Optimisation',
  description: 'Help your site show up in Google and look great when shared on social media.',
  tips: [
    'Fill in the SEO Title and Description — these are what people see in Google results.',
    'Upload a Social Sharing Image so your site looks great when shared on Facebook, Twitter, or LinkedIn.',
    'Keep your SEO Title under 60 characters and Description under 160 characters.',
    'If you leave SEO Title empty, your site name will be used instead.',
    'The Google Verification Code lets you use Google Search Console to see how your site performs in search.',
  ],
};

export default function SeoPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const { site } = useSite(siteId);
  const { items, loading, create, update } = useContent(siteId, 'site');
  const { addToast } = useToast();

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [originalData, setOriginalData] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);

  const existingItem = items[0];

  const isDirty = JSON.stringify(formData) !== JSON.stringify(originalData);
  useUnsavedChanges(isDirty);

  useEffect(() => {
    if (existingItem) {
      const data = existingItem.data || {};
      // Only pick the SEO-related fields
      const seoData: Record<string, unknown> = {};
      for (const key of Object.keys(seoFields)) {
        seoData[key] = data[key] ?? '';
      }
      setFormData(seoData);
      setOriginalData(seoData);
    }
  }, [existingItem]);

  const handleChange = (name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!siteId) return;
    setSaving(true);
    try {
      if (existingItem) {
        // Merge SEO fields into the full site data so we don't clobber other fields
        const fullData = { ...(existingItem.data || {}), ...formData };
        const contentId = getContentId(existingItem.contentKey);
        await update(contentId, fullData);
      } else {
        await create(formData);
      }
      setOriginalData({ ...formData });
      addToast('success', 'SEO settings saved');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>SEO</h1>
        <div className="page-header-actions">
          {isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="editor-layout">
        <div className="editor-main">
          <div className="editor-form">
            {/* Google Preview */}
            <div style={{ marginBottom: 24, padding: 16, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 8, fontWeight: 600 }}>Google Preview</p>
              <div style={{ fontFamily: 'Arial, sans-serif' }}>
                <div style={{ fontSize: '1.1rem', color: '#1a0dab', marginBottom: 2 }}>
                  {(formData.metaTitle as string) || site?.siteName || 'Your Site Name'}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#006621', marginBottom: 4 }}>
                  {site?.subdomain ? `https://${site.subdomain}.topzite.net` : 'https://yoursite.topzite.net'}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#545454', lineHeight: 1.4 }}>
                  {(formData.metaDescription as string) || 'Add a description to control how your site appears in search results.'}
                </div>
              </div>
            </div>

            {Object.entries(seoFields).map(([name, fieldSchema]) => (
              <FormField
                key={name}
                name={name}
                schema={fieldSchema}
                value={formData[name]}
                onChange={handleChange}
                siteId={siteId!}
              />
            ))}
          </div>
        </div>
        <HelpSidebar title={seoHelp.title} description={seoHelp.description} tips={seoHelp.tips} />
      </div>
    </div>
  );
}
